import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Stack, Switch, Typography } from '@mui/material';
import type { FeatureFlagClientConfigurationPanelProps } from '../../../types/features/featureFlags';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import { FEATURE_FLAG_CLIENT_LABELS } from '../constants/featureFlagOptions';
import FeatureFlagAssignmentTargetPanel from './FeatureFlagAssignmentTargetPanel';

const FeatureFlagClientConfigurationPanel: React.FC<FeatureFlagClientConfigurationPanelProps> = ({
  activeClient,
  assignmentIdsLoading,
  configuration,
  onAssignmentTargetChange,
  onBulkStoreAssignmentChange,
  onBulkUserAssignmentChange,
  onGlobalEnabledChange,
  onStoreAssignmentChange,
  onStoreSearchChange,
  onStoreStatusFilterChange,
  onUserAssignmentChange,
  storeIdsError,
  userIdsError,
  storeOptionsError,
  storeOptionsLoading,
  storeSearch,
  storeStatusFilter,
  stores,
}) => (
  <Box
    sx={{
      backgroundColor: adminColors.surfaceAlt,
      border: `1px solid ${adminColors.border}`,
      borderRadius: '8px',
      p: 1.5,
    }}
  >
    <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text, mb: 1.25 }}>
      {FEATURE_FLAG_CLIENT_LABELS[activeClient]} Configuration
    </Typography>

    {!configuration.selected ? (
      <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
        Select {FEATURE_FLAG_CLIENT_LABELS[activeClient]} to configure global state and assignments.
      </Typography>
    ) : (
      <Stack spacing={1.75}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            backgroundColor: adminColors.surface,
            border: `1px solid ${adminColors.border}`,
            borderRadius: '8px',
            px: 1.25,
            py: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
              Global State
            </Typography>
            <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, mt: 0.25 }}>
              {configuration.globalEnabled
                ? 'Feature is enabled for all stores and users.'
                : 'Feature is limited to selected stores or users.'}
            </Typography>
          </Box>
          <Switch
            checked={configuration.globalEnabled}
            onChange={(event) => onGlobalEnabledChange(event.target.checked)}
            inputProps={{ 'aria-label': `${FEATURE_FLAG_CLIENT_LABELS[activeClient]} global state` }}
          />
        </Stack>

        {configuration.globalEnabled ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'flex-start',
              backgroundColor: adminColors.infoBackground,
              border: `1px solid ${adminColors.primarySoft}`,
              borderRadius: '8px',
              color: adminColors.text,
              px: 1.25,
              py: 1,
            }}
          >
            <InfoOutlinedIcon sx={{ color: adminColors.primary, fontSize: 18, mt: 0.1 }} />
            <Typography sx={{ ...adminTypography.body, color: adminColors.text }}>
              No store or user selection is needed while Global State is on.
            </Typography>
          </Stack>
        ) : (
          <FeatureFlagAssignmentTargetPanel
            assignmentIdsLoading={assignmentIdsLoading}
            configuration={configuration}
            storeSearch={storeSearch}
            stores={stores}
            onAssignmentTargetChange={onAssignmentTargetChange}
            onBulkStoreAssignmentChange={onBulkStoreAssignmentChange}
            onBulkUserAssignmentChange={onBulkUserAssignmentChange}
            onStoreAssignmentChange={onStoreAssignmentChange}
            onStoreSearchChange={onStoreSearchChange}
            onStoreStatusFilterChange={onStoreStatusFilterChange}
            onUserAssignmentChange={onUserAssignmentChange}
            storeIdsError={storeIdsError}
            userIdsError={userIdsError}
            storeOptionsError={storeOptionsError}
            storeOptionsLoading={storeOptionsLoading}
            storeStatusFilter={storeStatusFilter}
          />
        )}
      </Stack>
    )}
  </Box>
);

export default React.memo(FeatureFlagClientConfigurationPanel);
